/**
 * ─── Common Scope Utilities ──────────────────────────────────────────────
 * Shared scope management for all stores (mutations, chat, etc).
 * Provides generic scope interface and deterministic key generation.
 *
 * Usage:
 *  - Stores that need scoping extend the generic Scope interface
 *  - Call scopeToKey() to generate deterministic cache keys
 *  - Enables lazy store registry pattern: Map<ScopeKey, Store>
 */

/**
 * Generic scope interface. Stores can extend this with domain-specific dimensions.
 * All scope keys are strings to enable scoping isolation.
 */
export interface Scope {
  [key: string]: string;
}

/**
 * Unique identifier for a scope, derived from sorted scope dimensions.
 * Format: "key1=value1|key2=value2" (sorted alphabetically by key).
 * Enables consistent Map key generation across render cycles.
 */
export type ScopeKey = string;

/**
 * Shared fallback scope used when a consumer does not provide explicit scoping.
 * Keeps behavior deterministic while allowing simple store access.
 */
export const DEFAULT_SCOPE: Scope = {
  __scope: "default",
};

/** Canonical key for DEFAULT_SCOPE. */
export const DEFAULT_SCOPE_KEY: ScopeKey = "__scope=default";

/**
 * Generate a deterministic, sorted key from any scope object.
 * Sorting ensures the same scope always produces the same key,
 * regardless of property order in the input object.
 *
 * @param scope - Any object with string key-value pairs
 * @returns Canonical scope key: "key1=value1|key2=value2" (alphabetically sorted)
 *
 * Example:
 *   scopeToKey({ patientId: "p1", locationId: "loc1" })
 *   => "locationId=loc1|patientId=p1"
 */
export function scopeToKey(scope: Scope): ScopeKey {
  return Object.entries(scope)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([k, v]) => `${k}=${v}`)
    .join("|");
}

/** Backward compatible alias for scopeToKey */
export const toScopeKey = scopeToKey;
